import { PULL_FENSTER_TAGE, brandingLesen, formSchemaSchema } from '@uep/shared';
import { alleSeiten, api } from './api';
import { METAS, db, metaLesen, metaSchreiben, objektSuchtext } from './db';
import { einheitenHeilen } from './einheiten';
import { profilPullen } from './profil';
import type {
  Flagge,
  LokaleEinheit,
  LokalerRaum,
  LokalerTermin,
  LokalesDokument,
  LokalesObjekt,
  LokalesProtokoll,
  LokalesTemplate,
} from './typen';

/**
 * Pull beim Login und bei jedem Reconnect (SPEC Kap. 4).
 *
 * Geladen werden: alle Vorlagen, alle Objekte mit ihren Räumen sowie die
 * Protokoll-Metadaten der letzten 90 Tage. Fotos werden bewusst **nicht**
 * vorgeladen — sie sind der größte Posten und werden nur bei Bedarf geholt.
 *
 * Lokale Änderungen gewinnen: Ein Datensatz mit `synchronisiert = 0` wird vom
 * Pull nicht überschrieben, sonst ginge Arbeit vor Ort verloren.
 */

export interface PullErgebnis {
  templates: number;
  objekte: number;
  einheiten: number;
  raeume: number;
  protokolle: number;
  dokumente: number;
  termine: number;
  konflikte: number;
}

const alsFlagge = (wert: unknown): Flagge => (wert === true ? 1 : 0);

const alsJson = <T>(wert: unknown, standard: T): T => {
  if (wert === null || wert === undefined) return standard;
  if (typeof wert === 'string') {
    try {
      return JSON.parse(wert) as T;
    } catch {
      return standard;
    }
  }
  return wert as T;
};

/**
 * Vorlagen holen — **auch die inaktiven**.
 *
 * Inaktiv heißt „noch nicht für neue Protokolle freigegeben“, nicht
 * „unsichtbar“: Ein Word-Import entsteht bewusst als Entwurf und muss im
 * Vorlagenbereich auftauchen, damit er sich prüfen und freigeben lässt. Vorher
 * filterte der Pull sie weg — der Import meldete Erfolg, die Vorlage war aber
 * nirgends zu sehen und ließ sich deshalb auch nie aktivieren.
 *
 * Aus der Protokollerfassung bleiben sie draußen: `ProtokollAnlegen` und der
 * Excel-Import fragen `db.templates.where('aktiv').equals(1)`.
 */
const holeTemplates = async (): Promise<number> => {
  const templates = await alleSeiten((token) =>
    api().models.Template.list({
      limit: 100,
      nextToken: token,
    }),
  );

  const eintraege: LokalesTemplate[] = [];

  for (const template of templates) {
    const versionen = await alleSeiten((token) =>
      api().models.TemplateVersion.list({
        filter: { templateId: { eq: template.id } },
        limit: 100,
        nextToken: token,
      }),
    );

    // Nur die aktuelle Version wird lokal vorgehalten. Ältere Versionen werden
    // bei Bedarf einzeln nachgeladen — sie sind unveränderlich und werden nur
    // zum Anzeigen alter Protokolle gebraucht.
    const aktuelle =
      versionen.find((version) => version.id === template.aktuelleVersionId) ??
      versionen.sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];

    if (!aktuelle) continue;

    const geprueftesSchema = formSchemaSchema.safeParse(alsJson(aktuelle.formSchema, {}));
    if (!geprueftesSchema.success) continue;

    eintraege.push({
      id: aktuelle.id,
      templateId: template.id,
      name: template.name,
      art: template.art,
      beschreibung: template.beschreibung ?? null,
      version: aktuelle.version ?? 1,
      aktiv: alsFlagge(template.aktiv),
      system: alsFlagge(template.system),
      formSchema: geprueftesSchema.data,
      layoutHtml: aktuelle.layoutHtml ?? '',
      layoutCss: aktuelle.layoutCss ?? '',
      // Tolerant lesen: Eine Vorlage mit unbrauchbarem Logo soll noch benutzbar
      // sein, statt beim Synchronisieren komplett auszufallen.
      branding: brandingLesen(aktuelle.branding),
      erstelltAm: aktuelle.erstelltAm ?? new Date().toISOString(),
    });
  }

  await db.templates.bulkPut(eintraege);
  return eintraege.length;
};

const holeObjekte = async (): Promise<{
  objekte: number;
  einheiten: number;
  raeume: number;
}> => {
  const objekte = await alleSeiten((token) =>
    api().models.Objekt.list({ limit: 200, nextToken: token }),
  );
  const lokale = await db.objekte.toArray();
  const nichtSynchronisiert = new Set(
    lokale.filter((objekt) => objekt.synchronisiert === 0).map((objekt) => objekt.id),
  );

  const eintraege: LokalesObjekt[] = objekte
    .filter((objekt) => !nichtSynchronisiert.has(objekt.id))
    .map((objekt) => ({
      id: objekt.id,
      objektNr: objekt.objektNr,
      bezeichnung: objekt.bezeichnung,
      strasse: objekt.strasse ?? null,
      plz: objekt.plz ?? null,
      ort: objekt.ort ?? null,
      lage: objekt.lage ?? null,
      wohnflaeche: objekt.wohnflaeche ?? null,
      zimmeranzahl: objekt.zimmeranzahl ?? null,
      eigentuemer: objekt.eigentuemer ?? null,
      notiz: objekt.notiz ?? null,
      aktiv: alsFlagge(objekt.aktiv ?? true),
      schluessel: alsJson(objekt.schluessel, null),
      suchtext: objekt.suchtext ?? objektSuchtext(objekt),
      lokalGeaendertAm: objekt.updatedAt ?? new Date().toISOString(),
      synchronisiert: 1,
      geloescht: 0,
    }));

  await db.objekte.bulkPut(eintraege);

  const einheiten = await alleSeiten((token) =>
    api().models.Einheit.list({ limit: 400, nextToken: token }),
  );
  const lokaleEinheiten = await db.einheiten.toArray();
  const offeneEinheiten = new Set(
    lokaleEinheiten.filter((einheit) => einheit.synchronisiert === 0).map((einheit) => einheit.id),
  );

  const einheitEintraege: LokaleEinheit[] = einheiten
    .filter((einheit) => !offeneEinheiten.has(einheit.id))
    .map((einheit) => ({
      id: einheit.id,
      objektId: einheit.objektId,
      bezeichnung: einheit.bezeichnung,
      einheitNr: einheit.einheitNr ?? null,
      art: einheit.art ?? null,
      etage: einheit.etage ?? null,
      lage: einheit.lage ?? null,
      wohnflaeche: einheit.wohnflaeche ?? null,
      zimmeranzahl: einheit.zimmeranzahl ?? null,
      reihenfolge: einheit.reihenfolge ?? 999,
      notiz: einheit.notiz ?? null,
      aktiv: alsFlagge(einheit.aktiv ?? true),
      schluessel: alsJson(einheit.schluessel, null),
      lokalGeaendertAm: einheit.updatedAt ?? new Date().toISOString(),
      synchronisiert: 1,
      geloescht: 0,
    }));

  await db.einheiten.bulkPut(einheitEintraege);

  const raeume = await alleSeiten((token) =>
    api().models.Raum.list({ limit: 400, nextToken: token }),
  );
  const lokaleRaeume = await db.raeume.toArray();
  const offeneRaeume = new Set(
    lokaleRaeume.filter((raum) => raum.synchronisiert === 0).map((raum) => raum.id),
  );

  const raumEintraege: LokalerRaum[] = raeume
    .filter((raum) => !offeneRaeume.has(raum.id))
    .map((raum) => ({
      id: raum.id,
      objektId: raum.objektId,
      einheitId: raum.einheitId ?? null,
      name: raum.name,
      flaeche: raum.flaeche ?? null,
      reihenfolge: raum.reihenfolge ?? 999,
      boden: raum.boden ?? null,
      wand: raum.wand ?? null,
      decke: raum.decke ?? null,
      ausstattung: alsJson(raum.ausstattung, null),
      lokalGeaendertAm: raum.updatedAt ?? new Date().toISOString(),
      synchronisiert: 1,
      geloescht: 0,
    }));

  await db.raeume.bulkPut(raumEintraege);
  return {
    objekte: eintraege.length,
    einheiten: einheitEintraege.length,
    raeume: raumEintraege.length,
  };
};

/**
 * Protokolle der letzten 90 Tage.
 *
 * Konfliktbehandlung (PHASENPLAN 1.9): Ist ein Protokoll serverseitig bereits
 * `SIGNED`, liegt lokal aber noch ein abweichender Entwurf, wird der Entwurf
 * **nicht** überschrieben, sondern als Konfliktkopie gesichert.
 */
const holeProtokolle = async (): Promise<{ protokolle: number; konflikte: number }> => {
  const grenze = new Date(Date.now() - PULL_FENSTER_TAGE * 24 * 60 * 60 * 1000).toISOString();

  const protokolle = await alleSeiten((token) =>
    api().models.Protocol.list({
      filter: { or: [{ updatedAt: { ge: grenze } }, { status: { eq: 'DRAFT' } }] },
      limit: 200,
      nextToken: token,
    }),
  );

  let uebernommen = 0;
  let konflikte = 0;

  for (const fern of protokolle) {
    const lokal = await db.protokolle.get(fern.id);
    const fernStatus = (fern.status ?? 'DRAFT') as LokalesProtokoll['status'];

    const uebersetzt: LokalesProtokoll = {
      id: fern.id,
      objektId: fern.objektId,
      einheitId: fern.einheitId ?? null,
      templateVersionId: fern.templateVersionId,
      protokollNr: fern.protokollNr,
      status: fernStatus,
      art: fern.art,
      uebergabeDatum: fern.uebergabeDatum ?? null,
      data: alsJson(fern.data, {}),
      beteiligte: alsJson(fern.beteiligte, []),
      dataHash: fern.dataHash ?? null,
      pdfKey: fern.pdfKey ?? null,
      vorgaengerId: fern.vorgaengerId ?? null,
      vorprotokollId: fern.vorprotokollId ?? null,
      erfasstVon: fern.erfasstVon ?? null,
      geraeteId: fern.geraeteId ?? null,
      aufbewahrungBis: fern.aufbewahrungBis ?? null,
      revision: fern.revision ?? 0,
      lokalGeaendertAm: fern.updatedAt ?? new Date().toISOString(),
      serverGeaendertAm: fern.updatedAt ?? null,
      synchronisiert: 1,
      konfliktkopie: 0,
      konfliktGrund: null,
      geloescht: 0,
    };

    if (!lokal) {
      await db.protokolle.put(uebersetzt);
      uebernommen += 1;
      continue;
    }

    if (lokal.konfliktkopie === 1) continue;

    const lokalOffen = lokal.synchronisiert === 0;
    const fernAbgeschlossen = fernStatus !== 'DRAFT';

    if (lokalOffen && fernAbgeschlossen) {
      // Der Server hat das letzte Wort — aber der lokale Stand geht nicht
      // verloren, sondern bleibt als eigene, sichtbare Kopie erhalten.
      await db.protokolle.put({
        ...lokal,
        id: `${lokal.id}--konflikt`,
        konfliktkopie: 1,
        konfliktGrund:
          'Dieses Protokoll wurde auf einem anderen Gerät bereits unterschrieben. ' +
          'Ihre lokalen Änderungen sind hier als Kopie gesichert.',
        synchronisiert: 1,
      });
      await db.outbox.where('entitaetId').equals(lokal.id).delete();
      await db.protokolle.put(uebersetzt);
      konflikte += 1;
      continue;
    }

    if (lokalOffen) {
      // Lokal offen, serverseitig noch Entwurf: lokaler Stand hat Vorrang
      // (Single-Writer, deterministisches Last-Write-Wins).
      continue;
    }

    await db.protokolle.put(uebersetzt);
    uebernommen += 1;
  }

  return { protokolle: uebernommen, konflikte };
};

const holeDokumente = async (): Promise<number> => {
  const dokumente = await alleSeiten((token) =>
    api().models.Document.list({ limit: 400, nextToken: token }),
  );
  const lokale = await db.dokumente.toArray();
  const offene = new Set(lokale.filter((d) => d.synchronisiert === 0).map((d) => d.id));

  const eintraege: LokalesDokument[] = dokumente
    .filter((dokument) => !offene.has(dokument.id))
    .map((dokument) => ({
      id: dokument.id,
      objektId: dokument.objektId,
      protokollId: dokument.protocolId ?? null,
      typ: dokument.typ ?? 'SONSTIGE',
      titel: dokument.titel,
      beschreibung: dokument.beschreibung ?? null,
      s3Key: dokument.s3Key ?? null,
      mimeType: dokument.mimeType ?? null,
      groesseBytes: dokument.groesseBytes ?? null,
      aufgenommenAm: dokument.aufgenommenAm ?? null,
      geoLat: dokument.geoLat ?? null,
      geoLng: dokument.geoLng ?? null,
      tags: (dokument.tags ?? []).filter((tag): tag is string => typeof tag === 'string'),
      inhalt: dokument.inhalt ?? null,
      erstelltAm: dokument.createdAt ?? new Date().toISOString(),
      lokalGeaendertAm: dokument.updatedAt ?? new Date().toISOString(),
      synchronisiert: 1,
      geloescht: 0,
    }));

  await db.dokumente.bulkPut(eintraege);
  return eintraege.length;
};

const holeTermine = async (): Promise<number> => {
  const termine = await alleSeiten((token) =>
    api().models.Termin.list({ limit: 200, nextToken: token }),
  );
  const lokale = await db.termine.toArray();
  const offene = new Set(lokale.filter((t) => t.synchronisiert === 0).map((t) => t.id));

  const eintraege: LokalerTermin[] = termine
    .filter((termin) => !offene.has(termin.id))
    .map((termin) => ({
      id: termin.id,
      objektId: termin.objektId,
      protokollId: termin.protocolId ?? null,
      titel: termin.titel,
      art: termin.art ?? null,
      beginn: termin.beginn,
      ende: termin.ende ?? null,
      ort: termin.ort ?? null,
      notiz: termin.notiz ?? null,
      teilnehmer: alsJson(termin.teilnehmer, null),
      zugewiesenAn: termin.zugewiesenAn ?? null,
      erledigt: alsFlagge(termin.erledigt),
      lokalGeaendertAm: termin.updatedAt ?? new Date().toISOString(),
      synchronisiert: 1,
      geloescht: 0,
    }));

  await db.termine.bulkPut(eintraege);
  return eintraege.length;
};

/**
 * Eigenes Profil nachziehen.
 *
 * Kein Teil von `PullErgebnis`: Es ist genau ein Datensatz, und ein Fehler hier
 * darf den restlichen Pull nicht abbrechen — ohne Profil ist die App voll
 * benutzbar, es fehlt nur die Vorbelegung.
 */
const holeProfil = async (): Promise<void> => {
  const schluessel = await metaLesen<string | null>(METAS.nutzerSchluessel, null);
  if (!schluessel) return;
  try {
    await profilPullen(schluessel);
  } catch {
    // Still: Das Profil kommt beim nächsten Sync mit.
  }
};

export const pullAusfuehren = async (): Promise<PullErgebnis> => {
  const templates = await holeTemplates();
  const { objekte, einheiten, raeume } = await holeObjekte();
  const { protokolle, konflikte } = await holeProtokolle();
  const dokumente = await holeDokumente();
  const termine = await holeTermine();
  await holeProfil();

  // Nach dem Pull: Objekte ohne Einheit bekommen ihre Standardeinheit, Räume
  // ohne Einheitenbezug werden dort eingehängt. Erst jetzt, weil vorher nicht
  // feststeht, welche Einheiten der Server schon kennt.
  await einheitenHeilen();

  await metaSchreiben(METAS.letzterPull, new Date().toISOString());

  return { templates, objekte, einheiten, raeume, protokolle, dokumente, termine, konflikte };
};
